import { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import type { MotionValue } from 'framer-motion'
import { GlobalEvents } from '@highlight/ipc'
import { refreshInteractiveBounds } from '@highlight/ui'
import type { SidepanelSide } from '@shared/sidepanel/side'
import { sendSidepanelBlurBounds } from '@renderer/overlay/utils/drag-ping'
import type { SidepanelVerticalLayout } from '../sidepanel-vertical-layout'

/** Cap on how often a running panel animation re-measures interactive bounds. */
const MOTION_BOUNDS_UPDATE_INTERVAL_MS = 32

interface SidepanelGeometryPublisherOptions {
  /** True only once the panel is both open and settled into its open motion state. */
  isOpen: boolean
  side: SidepanelSide
  sidepanelWidth: number
  viewportWidth: number
  verticalLayout: SidepanelVerticalLayout
  /** Live vertical offset of a reposition drag, in panel-root space. */
  dragY: MotionValue<number>
  isRepositioning: boolean
}

/**
 * Publishes where the panel is to everything outside React that has to track
 * it: the main process (swipe hit-testing, native blur) and the interactive
 * bounds registry.
 *
 * Returns the two motion callbacks the panel root wires to framer, which keep
 * the registry current while the panel is mid-animation.
 */
export function useSidepanelGeometryPublisher({
  isOpen,
  side,
  sidepanelWidth,
  viewportWidth,
  verticalLayout,
  dragY,
  isRepositioning,
}: SidepanelGeometryPublisherOptions) {
  // Emit sidepanel bounds on state change for swipe gesture detection and
  // main-process blur positioning.
  //
  // A layout effect, deliberately: this is what starts the native blur behind
  // the panel, and the blur has to start moving the instant the panel does.
  // Passive effects flush *after* paint, so as a `useEffect` this told the blur
  // to move only once the panel's first animated frame was already on screen —
  // the blur visibly trailed the glass, and because the surface is only 95%
  // opaque it read as the panel's whole interior lagging its edge.
  useLayoutEffect(() => {
    const width = isOpen ? sidepanelWidth : 0
    GlobalEvents.emit('sidepanel:bounds', {
      x: side === 'right' ? viewportWidth - width : 0,
      y: verticalLayout.top,
      width,
      height: verticalLayout.height,
    })
    // viewportWidth keeps the blur rect current after a display move/resize.
  }, [isOpen, sidepanelWidth, side, viewportWidth, verticalLayout.top, verticalLayout.height])

  // `dragY` updates outside React, so publish the live geometry separately for
  // the native blur. This avoids writing high-frequency drag frames into
  // GlobalState while keeping the glass directly behind the moving panel.
  useEffect(() => {
    if (!isRepositioning) {
      return
    }

    let animationFrame: number | null = null
    let latestOffset = dragY.get()
    const publish = () => {
      animationFrame = null
      sendSidepanelBlurBounds({
        x: side === 'right' ? viewportWidth - sidepanelWidth : 0,
        y: verticalLayout.top + latestOffset,
        width: sidepanelWidth,
        height: verticalLayout.height,
      })
    }
    const schedulePublish = (offset: number) => {
      latestOffset = offset
      if (animationFrame === null) {
        animationFrame = requestAnimationFrame(publish)
      }
    }

    schedulePublish(latestOffset)
    const unsubscribe = dragY.on('change', schedulePublish)
    return () => {
      unsubscribe()
      if (animationFrame !== null) {
        cancelAnimationFrame(animationFrame)
      }
    }
  }, [dragY, isRepositioning, side, sidepanelWidth, viewportWidth, verticalLayout.height, verticalLayout.top])

  const lastMotionBoundsRefreshAt = useRef(0)

  const onMotionUpdate = useCallback(() => {
    const now = performance.now()
    if (now - lastMotionBoundsRefreshAt.current < MOTION_BOUNDS_UPDATE_INTERVAL_MS) {
      return
    }
    lastMotionBoundsRefreshAt.current = now
    refreshInteractiveBounds()
  }, [])

  const onMotionComplete = useCallback(() => {
    lastMotionBoundsRefreshAt.current = 0
    refreshInteractiveBounds()
  }, [])

  return { onMotionUpdate, onMotionComplete }
}
